/**
 * Service for integrating shop items with character equipment
 * Handles conversion between shop items and character equipment/weapons/armor
 */

import { nanoid } from 'nanoid';
import type { ShopItem } from './ShopService';
import type { Equipment, Weapon, Armor, RateOfFire } from '../types/character';
import { EquipmentType, WeaponClass, BodyLocation } from '../types/character';
import { AmmunitionService } from './AmmunitionService';
import { SINGLE_GRENADE, isLauncher, isPayload } from './LauncherPayloadService';
import { parseCommaSeparated } from '../utils/parseCommaSeparated';
import { resolveDamageType } from '../utils/parseDamageType';

export interface WeaponStats {
  damage: string;
  damageType?: string;
  penetration: number;
  range: number;
  rateOfFire: RateOfFire;
  clip: number;
  reload: string;
  special: string[];
}

export interface ArmorStats {
  armorPoints: number;
  locations: BodyLocation[];
  special: string[];
}

/**
 * Clip for a converted weapon.
 *
 * A grenade has no clip in the rulebooks — the field is "-" — so `weaponStats.clip ||
 * 10` left it as the string "-", which meant `weapon.clip > 0` was false and nothing
 * was ever consumed: one purchased frag grenade threw forever. A grenade's clip is
 * instead how many are owned, so it displays as "2/3", and the count comes from the
 * inventory: one purchased grenade is 1, and buying more stacks onto the same entry.
 *
 * Launchers print "Variable" for clip, because capacity depends on the round loaded.
 * They get 0 rather than 10: a launcher holds no ammunition of its own, the carried
 * grenades are the ammunition. Defaulting to 10 would make `weapon.clip > 0` true, so
 * the numeric ammo block would decrement a phantom clip and the launcher would "run
 * out" after ten shots however many grenades the character was carrying.
 */
function resolveClip(shopItem: { id?: string }, clip: number | string | undefined): number {
  if (typeof clip === 'number') return clip;
  if (shopItem.id && isPayload(shopItem.id)) return SINGLE_GRENADE;
  if (shopItem.id && isLauncher({ sourceId: shopItem.id })) return 0;
  return 10;
}

export class EquipmentIntegrationService {
  private static instance: EquipmentIntegrationService;

  private constructor() {}

  public static getInstance(): EquipmentIntegrationService {
    if (!EquipmentIntegrationService.instance) {
      EquipmentIntegrationService.instance = new EquipmentIntegrationService();
    }
    return EquipmentIntegrationService.instance;
  }

  /**
   * Convert a shop item to character equipment
   * Uses sourceId to reference the item in items.json instead of storing the name
   */
  public convertToEquipment(
    shopItem: ShopItem
  ): Equipment & { weaponModification?: boolean; armorModification?: boolean } {
    const shopItemExtended = shopItem;

    // Detect cybernetics from shop item type
    const equipmentType =
      shopItem.type === 'cybernetic' ? EquipmentType.Cybernetic : EquipmentType.Gear;

    // Carry over craftsmanship from shop data if present
    const craftsmanship = shopItemExtended.craftsmanship as Equipment['craftsmanship'];

    return {
      id: nanoid(),
      sourceId: shopItem.id, // Reference to items.json
      type: equipmentType,
      // Newly acquired gear is on the person but not in hand.
      carryState: 'carried',
      weight: shopItem.weight,
      availability: shopItem.availability,
      cost: shopItem.cost || 0,
      description: shopItem.description,
      rulebook: shopItem.rulebook,
      ...(craftsmanship && { craftsmanship }),
      // Preserve modification flags
      ...(shopItemExtended.weaponModification && { weaponModification: true }),
      ...(shopItemExtended.armorModification && { armorModification: true })
    };
  }

  /**
   * Convert a shop item to a weapon (for weapon subtypes)
   * Uses sourceId to reference the item in items.json instead of storing the name
   */
  public convertToWeapon(shopItem: ShopItem): Weapon {
    const weaponStats = this.extractWeaponStats(shopItem);
    const shopItemWithProficiency = shopItem;
    const normalizedWeaponClass = this.normalizeWeaponClass(shopItemWithProficiency.weaponClass);
    const proficiencyRequired = shopItemWithProficiency.proficiencyRequired;
    const craftsmanship = shopItemWithProficiency.craftsmanship as Equipment['craftsmanship'];
    const damageType = resolveDamageType({
      damageType: weaponStats.damageType,
      damage: weaponStats.damage
    });

    return {
      id: nanoid(),
      sourceId: shopItem.id, // Reference to items.json
      type: EquipmentType.Weapon,
      // Newly acquired: on the person, not yet drawn.
      carryState: 'carried',
      class: normalizedWeaponClass ?? this.getWeaponClass(shopItem.subtype || ''),
      damage: weaponStats.damage,
      // Resolved from either the explicit damageType field or the rulebook letter
      // on the damage expression ("1d10 R"). Left unset when the weapon has no
      // damage type at all - a webber or graviton gun has no damage roll, and
      // claiming Impact would silently give it the wrong critical table.
      ...(damageType && { damageType }),
      penetration: weaponStats.penetration,
      range: weaponStats.range,
      rateOfFire: weaponStats.rateOfFire,
      clip: weaponStats.clip,
      reload: weaponStats.reload,
      weight: shopItem.weight,
      availability: shopItem.availability,
      cost: shopItem.cost || 0,
      special: weaponStats.special,
      description: shopItem.description,
      rulebook: shopItem.rulebook,
      proficiencyRequired,
      ...(craftsmanship && { craftsmanship })
    };
  }

  /**
   * Convert a shop item to armor (for armor subtypes)
   * Uses sourceId to reference the item in items.json instead of storing the name
   */
  public convertToArmor(shopItem: ShopItem): Armor {
    const armorStats = this.extractArmorStats(shopItem);
    const craftsmanship = shopItem.craftsmanship as Equipment['craftsmanship'];

    return {
      id: nanoid(),
      sourceId: shopItem.id, // Reference to items.json
      type: EquipmentType.Armor,
      // Newly acquired: carried but not yet donned (matches the previous
      // behaviour, where `equipped` was left undefined).
      carryState: 'carried',
      worn: false,
      locations: armorStats.locations,
      armorPoints: armorStats.armorPoints,
      weight: shopItem.weight,
      availability: shopItem.availability,
      cost: shopItem.cost || 0,
      special: armorStats.special,
      description: shopItem.description,
      rulebook: shopItem.rulebook,
      ...(craftsmanship && { craftsmanship })
    };
  }

  /**
   * Determine if a shop item is a weapon based on its subtype
   */
  public isWeapon(shopItem: ShopItem): boolean {
    return shopItem.type === 'weapon';
  }

  /**
   * Determine if a shop item is armor based on its subtype
   */
  public isArmor(shopItem: ShopItem): boolean {
    return shopItem.type === 'armor';
  }

  /**
   * Determine if a shop item is unusual ammunition based on its ID
   * matching a known AmmunitionDefinition.
   */
  public isAmmunition(shopItem: ShopItem): boolean {
    return AmmunitionService.getDefinition(shopItem.id) !== undefined;
  }

  /**
   * Extract weapon statistics from description (basic parsing)
   */
  private extractWeaponStats(shopItem: ShopItem): WeaponStats {
    // Check if the shop item has weaponStats (parsed weapon data)
    const weaponStats = shopItem.weaponStats;

    if (weaponStats) {
      // Use the parsed weapon stats
      const special: string[] = [];

      // Weapon qualities arrive as either a comma-separated string or an array
      special.push(...parseCommaSeparated(weaponStats.special));

      return {
        damage: weaponStats.damage || '1d10',
        damageType: weaponStats.damageType,
        penetration: weaponStats.penetration || 0,
        range: parseInt(weaponStats.range?.replace(/\D/g, '') || '30'),
        rateOfFire: {
          single: weaponStats.rateOfFire?.single === 'S' ? 1 : 0,
          semiAuto: parseInt(weaponStats.rateOfFire?.semiAuto || '0'),
          fullAuto: parseInt(weaponStats.rateOfFire?.fullAuto || '0')
        },
        clip: resolveClip(shopItem, weaponStats.clip),
        reload: weaponStats.reload || 'Full',
        special: special
      };
    }

    // Fallback: parse from description (legacy method)
    const description = shopItem.description.toLowerCase();

    const defaults: WeaponStats = {
      damage: '1d10',
      // No damageType: this path only runs for items with no weaponStats at all
      // (great-weapon, dueling-pistol - see TODO.md). Inventing Impact would hide
      // the gap and pick the wrong critical table.
      penetration: 0,
      range: 30,
      rateOfFire: { single: 1, semiAuto: 0, fullAuto: 0 },
      clip: 30,
      reload: 'Full',
      special: []
    };

    // Equipment stats should come from structured data fields, not parsed from descriptions
    // Parsing removed - data should be properly structured

    // Extract special qualities from description
    const special: string[] = [];
    if (description.includes('reliable')) special.push('Reliable');
    if (description.includes('unreliable')) special.push('Unreliable');
    if (description.includes('blast')) special.push('Blast');
    if (description.includes('flame')) special.push('Flame');
    if (description.includes('power field')) special.push('Power Field');
    if (description.includes('mono')) special.push('Mono');
    if (description.includes('tearing')) special.push('Tearing');
    if (description.includes('accurate')) special.push('Accurate');
    if (description.includes('inaccurate')) special.push('Inaccurate');
    if (description.includes('scatter')) special.push('Scatter');
    if (description.includes('primitive')) special.push('Primitive');

    defaults.special = special;

    return defaults;
  }

  /**
   * Extract armor statistics from shop item
   */
  /**
   * items.json stores armour locations as plain strings, and 8 entries use the
   * collective forms "arms" and "legs" which are NOT members of BodyLocation.
   * Until this was validated, a `Partial<ArmorStats>` cast asserted the strings
   * were already enum members, so those values were injected unchecked and
   * silently matched no hit location - the armour gave zero protection there.
   */
  private static readonly ARMOR_LOCATION_ALIASES: Record<string, BodyLocation[]> = {
    arms: [BodyLocation.LeftArm, BodyLocation.RightArm],
    legs: [BodyLocation.LeftLeg, BodyLocation.RightLeg]
  };

  private toArmorLocations(raw?: string[]): BodyLocation[] | undefined {
    if (!raw) return undefined;
    const known = new Set<string>(Object.values(BodyLocation));
    const out: BodyLocation[] = [];
    for (const entry of raw) {
      if (known.has(entry)) {
        out.push(entry as BodyLocation);
      } else {
        const alias = EquipmentIntegrationService.ARMOR_LOCATION_ALIASES[entry];
        if (alias) out.push(...alias);
        // unrecognised values are dropped rather than cast into the enum
      }
    }
    return out.length > 0 ? Array.from(new Set(out)) : undefined;
  }

  private extractArmorStats(shopItem: ShopItem): ArmorStats {
    const description = shopItem.description.toLowerCase();
    const armorData = shopItem.armorData;

    // Basic defaults
    const defaults: ArmorStats = {
      armorPoints: armorData?.armorPoints ?? 2,
      locations: this.toArmorLocations(armorData?.locations) ?? [
        BodyLocation.Head,
        BodyLocation.Body,
        BodyLocation.LeftArm,
        BodyLocation.RightArm,
        BodyLocation.LeftLeg,
        BodyLocation.RightLeg
      ],
      special: armorData?.special ?? []
    };

    // Armor points should come from structured data fields, not parsed from descriptions
    // Parsing removed - data should be properly structured

    // Extract special qualities from description
    const special: string[] = [];
    if (description.includes('environmentally sealed')) special.push('Environmentally Sealed');
    if (description.includes('sealed system')) special.push('Sealed System');
    if (description.includes('auto-stabilized')) special.push('Auto-Stabilized');
    if (description.includes('auto-senses')) special.push('Auto-Senses');
    if (description.includes('machine')) special.push('Machine');
    if (description.includes('powered')) special.push('Powered');
    if (description.includes('flexible')) special.push('Flexible');
    if (description.includes('poor quality')) special.push('Poor Quality');
    if (description.includes('best quality')) special.push('Best Quality');
    if (description.includes('vox')) special.push('Vox');
    if (description.includes('ablative')) special.push('Ablative');
    if (description.includes('ceramite')) special.push('Ceramite');
    if (description.includes('concealed')) special.push('Concealed');
    if (description.includes('reinforced')) special.push('Reinforced');

    defaults.special = Array.from(new Set([...defaults.special, ...special]));

    return defaults;
  }

  /**
   * Get weapon class from subtype
   */
  private getWeaponClass(subtype: string): WeaponClass {
    const classMap: Record<string, WeaponClass> = {
      Las: WeaponClass.Basic,
      Bolt: WeaponClass.Basic,
      Plasma: WeaponClass.Basic,
      Melta: WeaponClass.Basic,
      Flame: WeaponClass.Basic,
      Shock: WeaponClass.Basic,
      Launcher: WeaponClass.Heavy,
      Shotgun: WeaponClass.Basic,
      Basic: WeaponClass.Basic,
      Pistol: WeaponClass.Pistol,
      Power: WeaponClass.Melee,
      Chain: WeaponClass.Melee,
      Primitive: WeaponClass.Melee,
      Exotic: WeaponClass.Basic, // No exotic class in enum, using basic
      Melee: WeaponClass.Melee,
      'Primitive Weapons': WeaponClass.Melee,
      Needle: WeaponClass.Pistol
    };

    return classMap[subtype] || WeaponClass.Basic;
  }

  /**
   * Normalize a weapon class string to a WeaponClass enum value
   */
  private normalizeWeaponClass(weaponClass?: string): WeaponClass | null {
    if (!weaponClass) return null;

    const normalized = weaponClass.toLowerCase();
    switch (normalized) {
      case 'pistol':
        return WeaponClass.Pistol;
      case 'basic':
        return WeaponClass.Basic;
      case 'heavy':
        return WeaponClass.Heavy;
      case 'melee':
        return WeaponClass.Melee;
      case 'thrown':
        return WeaponClass.Thrown;
      default:
        return null;
    }
  }
}

// Export singleton instance
export const equipmentIntegrationService = EquipmentIntegrationService.getInstance();
